import type { ReactNode } from "react";
import { Empty } from "rizzui";

/**
 * 018: moved here from `apps/admin/src/components/`. It carries FR-028 - an empty list is never a
 * failure surface: no `role="alert"`, no destructive styling - which is a rule, so it sits with the
 * other rule-bearing components. `MainTable`'s own built-in "No Data" covers a table with no rows;
 * this is the surface that also offers the way out ("create the first one").
 *
 * `016` follow-up: replaces `@steelyard/ui`'s hand-rolled `EmptyState` (a muted circle + a small
 * `lucide-react` icon) with rizzui's own `Empty` component — the illustration isomorphic's real
 * list pages use for "no rows" states. Its default artwork already reads `fill-muted`/
 * `fill-background`/`fill-foreground` from this app's own tokens, so it inherits dark mode for
 * free. Same rules as before: never a failure surface (FR-028) — no `role="alert"`, no destructive
 * styling, `action` is where "create the first one" goes.
 */
export function EmptyState({
  children,
  title,
  action,
  icon,
}: {
  children: ReactNode;
  title?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div data-slot="empty-state" className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-muted px-6 py-12 text-center">
      <Empty
        image={icon}
        defaultImageClassName="h-28 w-auto"
        text={typeof title === "string" ? title : undefined}
        textClassName="mt-3 text-sm font-semibold text-foreground"
      >
        {typeof title !== "string" && title ? <p className="mt-3 text-sm font-semibold text-foreground">{title}</p> : null}
        <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">{children}</p>
        {action ? <div className="mt-3">{action}</div> : null}
      </Empty>
    </div>
  );
}
